export interface DayTripStop {
    stop_id: string
    stop_name: string
    /** Set when loaded from API; send in PUT for updates */
    day_trip_stop_id?: string | null
}

export interface DayTripSeatingPrice {
    capacity: number
    price_per_vehicle: number
    /** Set when loaded from API; send in PUT for updates */
    trip_pricing_id?: string | null
}

export interface DayTripDetailModel {
    day_trip_id: string
    trip_name: string
    day_trip_type_id: number | null
    day_trip_type_name: string
    /** City the trip belongs to. Drives which excursions/places appear in start/end/stops. */
    city_id: number | null
    city_name: string
    start_point_id: string
    start_point_name: string
    end_point_id: string
    end_point_name: string
    stops: DayTripStop[]
    flag_active: boolean
    seating_prices: DayTripSeatingPrice[]
    flag_enable_sic: boolean
    sic_price_per_person: number
    /** Rich-text itinerary stored as an HTML string. Optional. */
    day_trip_itinerary: string
    /** Dinner-transfer pricing block. Persisted via the `dinner_pricing` array in the mapper. */
    flag_enable_dinner?: boolean
    dinner_transfer_prices?: DayTripSeatingPrice[]
    /** SIC (per-person) price for dinner transfer; shown as the last "SIC" column when both
     *  Enable SIC and Enable Dinner Transfer are on. */
    dinner_transfer_sic_price_per_person?: number
}

/** Seating type from Generic API (e.g. seating_type_id: 1, seating_type_name: "4 Seater") */
export interface SeatingTypeItem {
    seating_type_id: number
    seating_type_name: string
}

/** Fallback when Generic API seating_type is not available */
export const SEATING_CAPACITIES = [4, 7, 16, 29, 35, 45] as const

/** Parse capacity from seating_type_name (e.g. "4 Seater" -> 4, "29 Seater" -> 29) */
export function parseCapacityFromSeatingName(seating_type_name: string): number {
    const match = seating_type_name?.match(/^\d+/)
    return match ? parseInt(match[0], 10) : 0
}

export interface SeatingMaps {
    capacityToSeatingTypeId: Record<number, number>
    seatingTypeIdToCapacity: Record<number, number>
    capacities: number[]
}

/** Build capacity <-> seating_type_id maps from Generic API seating_type array */
export function getSeatingMaps(seating_type: SeatingTypeItem[]): SeatingMaps {
    const capacityToSeatingTypeId: Record<number, number> = {}
    const seatingTypeIdToCapacity: Record<number, number> = {}
    const capacities: number[] = []
    for (const st of seating_type ?? []) {
        const cap = parseCapacityFromSeatingName(st.seating_type_name)
        if (cap > 0) {
            capacityToSeatingTypeId[cap] = st.seating_type_id
            seatingTypeIdToCapacity[st.seating_type_id] = cap
            capacities.push(cap)
        }
    }
    capacities.sort((a, b) => a - b)
    return { capacityToSeatingTypeId, seatingTypeIdToCapacity, capacities }
}

/** Fallback maps when seating_type is not loaded (matches API order: 4,7,16,29,35,45) */
const FALLBACK_CAPACITY_TO_ID: Record<number, number> = {
    4: 1, 7: 2, 16: 3, 29: 4, 35: 5, 45: 6,
}
const FALLBACK_ID_TO_CAPACITY: Record<number, number> = {
    1: 4, 2: 7, 3: 16, 4: 29, 5: 35, 6: 45,
}

export function getEmptyDayTrip(seating_type?: SeatingTypeItem[]): DayTripDetailModel {
    const maps = seating_type?.length
        ? getSeatingMaps(seating_type)
        : { capacityToSeatingTypeId: FALLBACK_CAPACITY_TO_ID, seatingTypeIdToCapacity: FALLBACK_ID_TO_CAPACITY, capacities: [...SEATING_CAPACITIES] }
    const seating_prices = maps.capacities.map((capacity) => ({
        capacity,
        price_per_vehicle: 0,
    }))
    const dinner_transfer_prices = maps.capacities.map((capacity) => ({
        capacity,
        price_per_vehicle: 0,
    }))
    return {
        day_trip_id: "",
        trip_name: "",
        day_trip_type_id: null,
        day_trip_type_name: "",
        city_id: null,
        city_name: "",
        start_point_id: "",
        start_point_name: "",
        end_point_id: "",
        end_point_name: "",
        stops: [],
        flag_active: true,
        seating_prices,
        flag_enable_sic: true,
        sic_price_per_person: 0,
        day_trip_itinerary: "",
        flag_enable_dinner: false,
        dinner_transfer_prices,
        dinner_transfer_sic_price_per_person: 0,
    }
}
